import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Rocket,
  ArrowRight,
  Settings,
  PlayCircle,
  BookOpen,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';

// Provider labels for display
const PROVIDER_LABELS: Record<string, string> = {
  openrouter: 'OpenRouter',
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
  bedrock: 'AWS Bedrock',
  azure: 'Azure OpenAI',
};

interface WelcomeStepProps {
  organizationName: string;
  teamName?: string;
  apiKeyConfigured: boolean;
  configuredProviders?: string[];
  onComplete: () => void;
}

export function WelcomeStep({
  organizationName,
  teamName,
  apiKeyConfigured,
  configuredProviders = [],
  onComplete,
}: WelcomeStepProps) {
  const navigate = useNavigate();

  // Use configuredProviders if available, otherwise fall back to apiKeyConfigured
  const hasApiKeys = configuredProviders.length > 0 || apiKeyConfigured;

  const handleGoToDashboard = () => {
    navigate('/home');
  };

  const handleGoToSettings = () => {
    navigate('/settings');
  };

  const handleGoToPlayground = () => {
    navigate('/playground');
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex justify-center">
          <div className="relative">
            <Rocket className="h-16 w-16 text-primary animate-bounce" />
          </div>
        </div>
        <h2 className="text-2xl font-semibold">You're All Set!</h2>
        <p className="text-sm text-muted-foreground">
          Welcome to {organizationName}
        </p>
      </div>

      <div className="bg-muted/50 rounded-lg p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-medium">
            ✓
          </div>
          <div>
            <p className="text-sm font-medium">Organization Created</p>
            <p className="text-xs text-muted-foreground">{organizationName}</p>
          </div>
        </div>

        {teamName && (
          <div className="flex items-start gap-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-medium">
              ✓
            </div>
            <div>
              <p className="text-sm font-medium">Team Joined</p>
              <p className="text-xs text-muted-foreground">{teamName}</p>
            </div>
          </div>
        )}

        <div className="flex items-start gap-3">
          <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
            hasApiKeys
              ? 'bg-primary/10 text-primary'
              : 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-500'
          }`}>
            {hasApiKeys ? '✓' : '!'}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">API Keys</p>
            {configuredProviders.length > 0 ? (
              <div className="flex flex-wrap gap-1 mt-1">
                {configuredProviders.map(provider => (
                  <Badge key={provider} variant="secondary" className="text-xs gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                    {PROVIDER_LABELS[provider] || provider}
                  </Badge>
                ))}
              </div>
            ) : hasApiKeys ? (
              <p className="text-xs text-muted-foreground">Configured</p>
            ) : (
              <div className="flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-500">
                <AlertTriangle className="h-3 w-3" />
                Not configured - add in Settings to enable AI features
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-medium">What's Next?</h3>

        <div className="grid gap-2">
          {hasApiKeys ? (
            <button
              onClick={handleGoToPlayground}
              className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
            >
              <PlayCircle className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <p className="text-sm font-medium">Try the Playground</p>
                <p className="text-xs text-muted-foreground">
                  Run agents and test your setup
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ) : (
            <button
              onClick={handleGoToSettings}
              className="flex items-center gap-3 p-3 rounded-lg border border-yellow-200 bg-yellow-50 hover:bg-yellow-100 dark:border-yellow-800 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30 transition-colors text-left"
            >
              <Settings className="h-5 w-5 text-yellow-600 dark:text-yellow-500" />
              <div className="flex-1">
                <p className="text-sm font-medium">Add API Key</p>
                <p className="text-xs text-muted-foreground">
                  Required to run AI agents and use the assistant
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
            </button>
          )}
        </div>
      </div>

      <Button onClick={onComplete} className="w-full">
        Get Started
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}
